import { CartService } from 'src/app/shared/services/cart.service';
import { CookiesService } from 'src/app/cookies/cookies.service';
import { ChatService } from './shared/services/chat.service';
import { AfterViewInit, Component, HostBinding, HostListener, Inject, NgZone, OnInit, Optional, Renderer2, ViewChild } from '@angular/core';
import { Router, NavigationStart, RouterEvent } from '@angular/router';
import { AuthService } from './auth/services/auth.service';
import { User } from './auth/models/user.model';
import { filter } from 'rxjs/operators';
import { InitService } from './shared/services/init.service';
import { ModalService } from './shared/services/modal.service';
import { UtilsService } from './shared/services/utils.service';
import { L10nTranslationService } from '@snack-dev/angular-l10n';
import { LocaleService } from './shared/services/locale.service';
import { SEOService } from './shared/services/seo.service';
import { l10nLocales } from './localization.module';
import { Bugme } from './shared/models/bugme.model';
import { MediaService } from './player/services/media.service';
import { ErrorsService } from './shared/services/errors.service';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { ReferralService } from './shared/services/referral.service';
import { GtagService } from './gtag/gtag.service';
import { DOCUMENT } from '@angular/common';
import { ExperimentsService } from './shared/services/experiments.service';
import { PopoverDirective } from 'ngx-bootstrap/popover';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {

  currentUser: User;
  playerCollapsed = false;

  userSubscribed = true;
  userAdmin = false;
  userEditor = false;

  headerDisplayName = '';

  bugme: Bugme = null;

  public showAddedToCart = false;
  public trackName: string;

  public year = new Date().getFullYear();

  referralUser: string;
  isReferralPath: boolean;
  isReferralQuery: boolean;
  path: string;
  referralUrl: string;
  params: URLSearchParams;


  /**
   * Disables animations for the entire app until the transition from server has completed.
   */
  @HostBinding('@.disabled')
  public animationsDisabled = true;

  organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Corporation',
    name: 'HookSounds',
    url: 'https://www.hooksounds.com/',
    logo: 'https://www.hooksounds.com/assets/home-images/hooksounds-logo-header.svg',
  };

  mobileMaxSize = 992;
  mobile = false;
  menuCollapsed = true;

  @ViewChild('popupCart') popover: PopoverDirective;

  constructor(
    private auth: AuthService,
    private router: Router,
    private init: InitService,
    public cart: CartService,
    public mediaService: MediaService,
    public chatService: ChatService,
    private modalService: ModalService,
    public utilsService: UtilsService,
    private referralService: ReferralService,
    private translation: L10nTranslationService,
    public locale: LocaleService,
    public seo: SEOService,
    private cookieService: CookiesService,
    private errorsService: ErrorsService,
    public experimentsService: ExperimentsService,
    public gtag: GtagService,
    private cartService: CartService,
    private renderer: Renderer2,
    private zone: NgZone,
    @Optional() @Inject(REQUEST) protected request: any,
    @Inject(DOCUMENT) private document: any
  ) {

    if (this.utilsService.isBrowser) {
      this.params = new URLSearchParams(window.location.search);
      this.referralUser = null;
      this.isReferralPath = window.location.pathname?.startsWith('/ref');
      this.isReferralQuery = this.params.has('ref');
      this.path = window.location.pathname;
      this.referralUrl = window.location.href;
    }

    this.auth.currentUserSubject.subscribe((u) => {
      this.currentUser = u;
      this.headerDisplayName = this.cookieService.get('displayName');
      if (!this.auth.isLoggedIn()) {
        this.userSubscribed = false;
        this.userAdmin = false;
      }
    });
    this.auth.subscription.subscribe((response) => {
      this.userSubscribed = response?.isSubscribed ?? false;
      this.userAdmin = response?.isAdmin ?? false;
      this.userEditor = response?.isEditor ?? false;
    });

  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.mobile = event.target.innerWidth <= this.mobileMaxSize;
  }

  ngOnInit() {

    if (this.utilsService.isBrowser) {
      // Enable animations after the site is loaded.
      setTimeout(() => {
        this.animationsDisabled = false;
      }, 500);
      this.scheduleExternalScripts();

      this.experimentsService.setAnalyticsExperiments();
    } else {
      this.document.documentElement.lang = this.locale.lang;
    }

    this.fetchInitData();
    this.setUpBugMeBar();
    this.initSEO();

    if (this.utilsService.isBrowser && window.innerWidth <= this.mobileMaxSize) {
      this.mobile = true;
    }

    this.cartService.addedToCart.subscribe(item => {
      this.showAddedToCart = this.experimentsService.isPopupAddedToCartVariant();
      if (this.showAddedToCart) {
        this.trackName = item.name;
        this.popover.show();
      }
    });
  }

  ngAfterViewInit() {
    this.updateCartPopupState();
  }

  scheduleExternalScripts() {
    this.zone.runOutsideAngular(() => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(this.initExternalScripts);
      } else {
        this.initExternalScripts();
      }
    });
  }

  initExternalScripts() {
    if (window.hasOwnProperty('initThirdParty')) {
      (window as any).initThirdParty();
    }
  }

  private initSEO() {
    if (this.utilsService.isBrowser) {
      // Subscribe to route change end for SEO meta tag
      // update and collapse nav menu.
      this.router.events
        .pipe(filter((event: RouterEvent) => event instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          if (this.utilsService.isBrowser) {
            this.experimentsService.setAnalyticsExperiments();
          }
          // If the user locale is not the default one and they load a default route URL there's an
          // extra NavigationStart that we have to ignore (if we don't page_view will be tracked twice).
          // To prevent this, we only update SEO when on the default locale event is fired.
          if (event.url && !event.url.startsWith('/' + this.locale.lang)) {
            this.seo.updateSEO(event.url);
          }
          this.collapseNav();
        });
      this.gtag.pageViewWithWindowParams();
    } else if (this.request) {
      this.seo.updateSEO(this.request.url);
    }
  }

  /**
   * Here we fetch the initial data which will be used accross all of the application.
   * We also retrieve and send the referral info to the referral service who does the referral tracking.
   */
  private fetchInitData() {

    this.init.fetch();

    // We only call the referral service from the browser because the server
    // doesn't have the window.location.href parameter that we need to track
    // referrals properly.
    if (this.utilsService.isBrowser) {
      if (this.isReferralPath) {
        const items = this.path.split('/', 4).filter((x) => x);
        if (items.length >= 2) {
          this.referralUser = items[1];
        }
      } else if (this.isReferralQuery) {
        this.referralUser = this.params.get('ref') ?? null;
      }

      if (this.referralUser) {
        const referralCampaign = this.params.has('campaign') ? this.params.get('campaign') : null;
        this.referralService.fetch(this.referralUser, referralCampaign, this.referralUrl);
      }
    }
  }

  /**
   * Initializes the BugMeBar jQuery library with the initial data response.
   */
  private setUpBugMeBar() {
    if (this.utilsService.isBrowser && window.innerWidth >= 1000) {
      this.init.data.subscribe((data) => {
        if (data.bugMeBar) {
          const bugme = data.bugMeBar as Bugme;
          if (bugme && bugme?.message) {
            this.bugme = data.bugMeBar;
          }
        }
      });
    }
  }

  /**
   * Cart popover experiment.
   */
  private updateCartPopupState() {
    if (this.utilsService.isBrowser && this.cart.totalItems > 0) {
      if (this.experimentsService.isPopupShoppingCartVariant()) {
        this.popover.show();
      }
    }
  }

  public closeCartPopup() {
    this.experimentsService.hidePopupShoppingCartVariant();
    this.popover.hide();
  }

  public goToCheckoutCartPopup() {
    this.popover.hide();
    this.router.navigate(['/checkout'], { queryParams: { reminder_popup: true }});
  }

  public goToCheckout(){
    this.router.navigate(['/checkout']);
    this.popover.hide();
  }

  public continueShopping() {
    this.popover.hide();
  }

  /**
   * Deletes the currently stored user session and redirects to the home screen.
   */
  logout() {
    this.auth.logout().subscribe(_ => {
      this.router.navigate(['/']);
    });
  }

  /**
   * Changes the currently active language, which is stored in LocalStorage.
   * Then it reloads the page so all endpoints get called again with the newly
   * selected language to get proper server-side translations.
   */
  async setLang(event: Event, lang: string) {
    event.preventDefault();
    for (const item of l10nLocales) {
      if (item.locale.language === lang) {
        await this.translation.setLocale(item.locale);
      }
    }
    if (this.seo.langUrls && this.seo.langUrls[lang]) {
      window.location.href = this.seo.langUrls[lang];
    } else {
      location.reload();
    }
  }

  /**
   * Redirects to the search page with a specific value in the search input field.
   */
  search(value: string) {
    this.collapseNav();
    this.router.navigate(['/royalty-free-music'], {
      queryParams: { search: value },
    });
  }

  /**
   * Closes the main navigation menu.
   * This should be called on every page change and click outside.
   */
  collapseNav() {
    if (this.utilsService.isBrowser) {
      const element: HTMLElement = document.getElementsByClassName('navbar-toggler')[0] as HTMLElement;
      if (element && element.getAttribute('aria-expanded') === 'true') {
        element.click();
      }
    }
  }

  /**
   * Opens Crisp chat.
   */
  openChat() {
    this.chatService.openChat();
  }

  /**
   * Admin-only functionality.
   * Pulls new translations from POEditor for all languages.
   */
  updateTranslations() {
    this.modalService.openTranslationsUpdater();
  }

  /**
   * Admin-only functionality.
   * Clears the API cache in the WP backend and reloads the page.
   */
  clearApiCache() {
    this.utilsService.clearApiCache().subscribe((_) => {
      location.reload();
    });
  }

  collapse(event: any) {
    this.playerCollapsed = event;
  }

  isPlayerVisible(): boolean {
    return !this.playerCollapsed && this.mediaService.track && !this.mediaService.track.isSoundEffect;
  }

  includePlayer(): boolean {
    return this.utilsService.isBrowser && this.mediaService.track && !this.mediaService.track?.isSoundEffect;
  }
}
